import React from 'react';

interface Props {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export default class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    console.error('[Digitix ErrorBoundary]', error, info.componentStack);
  }

  handleReset = (): void => {
    this.setState({ hasError: false, error: null });
  };

  render(): React.ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;
      return (
        <div className="flex flex-col items-center justify-center min-h-[60vh] gap-5 px-6">
          <div className="text-5xl opacity-40">⚠️</div>
          <div className="text-center">
            <h2 className="text-lg font-semibold text-dx-text mb-2">Something went wrong</h2>
            <p className="text-sm text-dx-muted max-w-sm">
              {this.state.error?.message ?? 'An unexpected error occurred.'}
            </p>
          </div>
          <button
            onClick={this.handleReset}
            className="px-5 py-2.5 rounded-xl text-sm font-semibold text-black bg-[linear-gradient(135deg,#25D366,#1BA350)] cursor-pointer border-none"
          >
            Try again
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
